import {
  createApi,
  FetchArgs,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react'

import { OMS_API_ENDPOINT, OMS_REDUCER_PATH } from './constants'

import { v4 as uuid } from 'uuid'
import { decodeToken } from './utils'

// Function to handle Set-Cookie headers and set them as browser cookies
const handleSetCookieHeaders = (response: Response) => {
  if (typeof window === 'undefined') return // Only run on client side

  const setCookieHeader = response.headers.get('set-cookie')

  if (!setCookieHeader) return

  // Split multiple cookies if present
  const cookiesToSet = setCookieHeader.split(',').map((cookie) => cookie.trim())

  for (const cookie of cookiesToSet) {
    // Parse the cookie string to extract name, value, and attributes
    const cookieParts = cookie.split(';')
    const nameValue = cookieParts[0]
    if (!nameValue) continue

    const [name, value] = nameValue.split('=')

    if (!name || !value) continue

    // Extract cookie attributes
    const attributes: Record<string, string> = {}
    for (let i = 1; i < cookieParts.length; i++) {
      const part = cookieParts[i]?.trim()
      if (!part) continue

      const [key, val] = part.split('=')
      if (key) {
        attributes[key.toLowerCase()] = val || ''
      }
    }

    // Build cookie string for document.cookie
    let cookieString = `${name}=${value}`

    if (attributes.path) {
      cookieString += `; path=${attributes.path}`
    }

    if (attributes.domain) {
      cookieString += `; domain=${attributes.domain}`
    }

    if (attributes.expires) {
      cookieString += `; expires=${attributes.expires}`
    }

    if (attributes['max-age']) {
      cookieString += `; max-age=${attributes['max-age']}`
    }

    // Only add Secure flag if not in development
    if (attributes.secure && process.env.NODE_ENV !== 'development') {
      cookieString += '; secure'
    }

    if (attributes.samesite) {
      cookieString += `; samesite=${attributes.samesite}`
    }

    // Set the cookie
    document.cookie = cookieString
  }
}

const staggeredBaseQueryWithBailOut = retry(
  async (args: string | FetchArgs, api, extraOptions) => {
    const result = await fetchBaseQuery({
      baseUrl: OMS_API_ENDPOINT,
      timeout: 10000,
      prepareHeaders: (headers, { getState }) => {
        const accessToken = window.localStorage.getItem('accessToken')
        const { clientId } = decodeToken(accessToken || '')

        headers.set('Content-Type', 'application/json')
        headers.set('Accept', 'application/json')
        headers.set('X-Request-Id', uuid())

        // Set Origin based on current protocol
        const origin =
          typeof window !== 'undefined' && window.location.protocol === 'https:'
            ? 'https://nomnom.localhost:3000'
            : 'http://nomnom.localhost:3000'
        headers.set('Origin', origin)

        if (accessToken) {
          headers.set('x-oddle-pass-token', accessToken)
          headers.set('x-oddle-pass-client-id', clientId)
        }

        // Explicitly set all cookies from document.cookie
        const cookieString = document.cookie
        if (cookieString) {
          headers.set('Cookie', cookieString)
        }

        return headers
      },
    })(args, api, extraOptions)

    // Handle Set-Cookie headers from the response
    if (result.data && result.meta?.response) {
      // Try different ways to access the response
      if (result.meta.response instanceof Response) {
        handleSetCookieHeaders(result.meta.response)
      } else {
        // If it's not a Response object, try to access headers as a plain object
        const responseObj = result.meta.response as any
        if (responseObj?.headers) {
          const setCookieHeader =
            responseObj.headers['set-cookie'] ||
            responseObj.headers['Set-Cookie']
          if (setCookieHeader) {
            // Handle the cookie setting manually
            if (typeof window !== 'undefined') {
              const cookiesToSet = Array.isArray(setCookieHeader)
                ? setCookieHeader
                : [setCookieHeader]
              for (const cookie of cookiesToSet) {
                document.cookie = cookie
              }
            }
          }
        }
      }
    }

    if (
      result.error &&
      typeof result.error.status === 'number' &&
      result.error.status < 500 &&
      result.error.status >= 400
    ) {
      retry.fail(result.error)
    }

    return result
  },
  { maxRetries: 1 },
)

export const omsApi = createApi({
  reducerPath: OMS_REDUCER_PATH,
  baseQuery: staggeredBaseQueryWithBailOut,
  tagTypes: ['MenuItems'],
  // Server-side memory: every SSR request builds its own seeded store
  // (makeSeededStore upserts getMenu/getAllStores entries). With the default
  // keepUnusedDataFor (60s), RTK Query schedules a removal timer per entry
  // whose closure keeps the WHOLE per-request store — several MB of menu +
  // products data — alive for a minute after the response has been sent.
  // Under crawler traffic that is rps x 60s x store-size of dead memory
  // (measured: +104MB retained after a 300-request burst vs the pre-seeding
  // build; 0 recovers ~70% of it). The client keeps the 60s default so
  // remounting components does not refetch.
  keepUnusedDataFor: typeof window === 'undefined' ? 0 : 60,
  endpoints: () => ({}),
})
